import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { useTenantInstance } from "@/contexts/TenantInstanceContext";
import { RUN_POLL_INTERVAL_LIVE_MS, usePollWhile } from "@/hooks/use-runs";
import { isTerminalRunStatus } from "@/lib/runs/runActivity";
import {
  LIST_INVALIDATION_INTERVAL_MS,
  invalidateRunFinished,
  useFeedTransport,
  useRunFeed,
  useSharedRunFeedAvailable,
} from "@/lib/live/InstanceRunFeed";

/**
 * #2005: the one rule that keeps a screen listing runs (or a summary of them) true when a run
 * starts or finishes while it is open, without opening the run.
 *
 * The tab's feed (`InstanceRunFeedProvider`) refreshes the run lists, the connections list, the
 * overview and the run history queries on a run's `RUN_RUNNING` and terminal frames, but a frame
 * arrives only while some component holds the feed. The Runs list, the Overview, Schedules and
 * Your data held none, so a finished run read Running there until the screen was mounted again.
 *
 * - **It holds the tab's feed while the screen is mounted**, so a run that starts or finishes at
 *   any time reaches the cache rule (the one shared socket, ref counted; nothing on the server
 *   changes, nor who hears what). Frames are sent only while a run moves.
 * - **It polls `poll`** for what no frame says: a queued run (its first frame is `RUN_RUNNING`), a
 *   cancel, retry or force reset from the web (no frame), a NOTIFY lost (at most once) or a socket
 *   that hangs or drops. Every 15 s while a listed run moves and the socket is not live, every 30 s
 *   otherwise. Never in a hidden tab (it reads once on coming back), never offline.
 * - **When the socket comes back** from a drop, it refreshes what a finish touches once, since the
 *   frames sent meanwhile are not replayed.
 * - **When `active` turns false** (a read showed the run finished), it refreshes what a terminal
 *   frame refreshes (`invalidateRunFinished`) once, unless that frame just did, so Last loaded,
 *   Success and the other screens' rows follow even when no frame came.
 */

/** The fallback poll while a listed run moves and the socket is not live. */
export const LIVE_LIST_POLL_MS = RUN_POLL_INTERVAL_LIVE_MS;
/** The safety poll otherwise. */
export const LIVE_LIST_SAFETY_POLL_MS = LIST_INVALIDATION_INTERVAL_MS;
/** A terminal frame this recent already refreshed what the flip would. */
const FRAME_COVERS_FLIP_MS = 10_000;

function hidden(): boolean {
  return typeof document !== "undefined" && document.visibilityState === "hidden";
}

export function useLiveRunRefresh({
  active,
  poll,
  scope = "",
}: {
  /** True while the screen shows a run that is queued or running. */
  active: boolean;
  /** Re-reads what the screen shows; omitted when the screen already polls on its own. */
  poll?: () => void;
  /**
   * What the screen lists (its filters and page). A change of scope moves `active` without any run
   * finishing, so it never counts as a finish.
   */
  scope?: string;
}): void {
  const { selectedInstance } = useTenantInstance();
  // Only the provider's feed refreshes the cache; a private one (a screen mounted without it, in a
  // test) would hold a socket for nothing, so the screen then relies on its poll alone.
  const shared = useSharedRunFeedAvailable();
  const anyFeed = useRunFeed(selectedInstance?.id ?? null);
  const feed = shared ? anyFeed : null;
  const transport = useFeedTransport(feed).transport;
  const queryClient = useQueryClient();
  const lastTerminalFrameAt = useRef(Number.NEGATIVE_INFINITY);

  useEffect(() => {
    if (!feed) return;
    const off = feed.onFrame((frame) => {
      if (isTerminalRunStatus(frame.runStatus)) lastTerminalFrameAt.current = Date.now();
    });
    const release = feed.acquire();
    return () => {
      off();
      release();
    };
  }, [feed]);

  const live = transport === "live";
  usePollWhile(
    poll !== undefined,
    active && !live ? LIVE_LIST_POLL_MS : LIVE_LIST_SAFETY_POLL_MS,
    () => {
      if (!hidden()) poll?.();
    },
  );

  // A tab coming back reads once: its polls were skipped while it was hidden.
  const latestPoll = useRef(poll);
  useEffect(() => {
    latestPoll.current = poll;
  });
  useEffect(() => {
    if (typeof document === "undefined") return;
    const onVisible = () => {
      if (hidden()) return;
      // Under the provider, showing the tab already refreshes what a finish touches (the feed's
      // own visibility rule), so the screen neither polls again nor counts its next read as a
      // finish of its own.
      if (shared) {
        lastTerminalFrameAt.current = Date.now();
        return;
      }
      latestPoll.current?.();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [shared]);

  // Back from a drop: the frames sent meanwhile are lost, so refresh once.
  const previousTransport = useRef(transport);
  useEffect(() => {
    const before = previousTransport.current;
    previousTransport.current = transport;
    if (transport === "live" && before === "reconnecting") invalidateRunFinished(queryClient);
  }, [transport, queryClient]);

  const wasActive = useRef({ active, scope });
  useEffect(() => {
    const previous = wasActive.current;
    wasActive.current = { active, scope };
    if (previous.scope !== scope) return;
    if (!previous.active || active) return;
    if (Date.now() - lastTerminalFrameAt.current < FRAME_COVERS_FLIP_MS) return;
    invalidateRunFinished(queryClient);
  }, [active, scope, queryClient]);
}
